import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramQBtuh } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.heatTransferQBtuh.v1';

type Fields = { cfm: string; tempDiff: string; heatLoad: string };
const EMPTY: Fields = { cfm: '', tempDiff: '', heatLoad: '' };

/**
 * Sensible Heat Load — Q = CFM × 1.08 × ΔT
 * Bidirectional: fill any 2 fields to solve for the third.
 * ΔT is a temperature difference — scale only, no offset.
 */
const HeatTransferQBtuhScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  // ΔT scale only (difference, no offset)
  const dtToImp = useCallback((s: string) => {
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return NaN;
    return isImperial ? n : n * 9 / 5;
  }, [isImperial]);
  const dtFromImp = useCallback((n: number, dec = 2) => {
    if (!Number.isFinite(n)) return '';
    return parseFloat((isImperial ? n : n * 5 / 9).toFixed(dec)).toString();
  }, [isImperial]);
  const dtUnit = isImperial ? '°F' : '°C';

  const calculate = useCallback(() => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const c = toImp(fields.cfm, 'flow');
    const t = dtToImp(fields.tempDiff);
    const q = toImp(fields.heatLoad, 'heatRate');

    if (fields.heatLoad === '') {
      if (!(c > 0) || !(t > 0)) { setError('Flow and ΔT must be greater than zero.'); return; }
      setSolvedFor('heatLoad');
      set({ heatLoad: fromImp(c * 1.08 * t, 'heatRate', 2) });
    } else if (fields.cfm === '') {
      if (!(t > 0)) { setError('ΔT must be greater than zero.'); return; }
      setSolvedFor('cfm');
      set({ cfm: fromImp(q / (1.08 * t), 'flow', 2) });
    } else if (fields.tempDiff === '') {
      if (!(c > 0)) { setError('Flow must be greater than zero.'); return; }
      setSolvedFor('tempDiff');
      set({ tempDiff: dtFromImp(q / (1.08 * c)) });
    }
  }, [fields, toImp, fromImp, dtToImp, dtFromImp, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() => [
    fields.cfm && { label: 'Flow', value: fields.cfm, unit: unit('flow'), emphasis: solvedFor === 'cfm' },
    fields.tempDiff && { label: 'ΔT', value: fields.tempDiff, unit: dtUnit, emphasis: solvedFor === 'tempDiff' },
    fields.heatLoad && { label: 'Heating/Cooling Load', value: fields.heatLoad, unit: unit('heatRate'), emphasis: solvedFor === 'heatLoad' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, unit, dtUnit, solvedFor]);

  return (
    <CalcShell
      title="Q BTU/h Calculation"
      subtitle={isImperial ? 'Solve for load, flow, or ΔT using Q = CFM × 1.08 × ΔT' : 'Solve for load, flow, or ΔT using Q = L/s × 1.21 × ΔT'}
      formula={
        <>
          <FormulaLine>{isImperial ? 'Q = CFM × 1.08 × ΔT' : 'Q = L/s × 1.21 × ΔT'}</FormulaLine>
          <FormulaNote>
            {isImperial
              ? `Flow = ${unit('flow')}, ΔT (${dtUnit}). Constant 1.08 = 0.075 lb/ft³ × 0.24 BTU/lb·°F × 60 min/hr.`
              : `Flow = ${unit('flow')}, ΔT (${dtUnit}). Constant 1.21 = 1.2 kg/m³ × 1.006 kJ/kg·°C (SI equivalent).`}
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter={isImperial ? 'Formula: Q = CFM × 1.08 × ΔT' : 'Formula: Q = L/s × 1.21 × ΔT'}
      diagram={<DiagramQBtuh />}
      outputSlots={{
        cfm: fields.cfm ? toImp(fields.cfm, 'flow') : undefined,
        delta_t_f: fields.tempDiff ? dtToImp(fields.tempDiff) : undefined,
        q_btuh: fields.heatLoad ? toImp(fields.heatLoad, 'heatRate') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.cfm) patch.cfm = fromImp(values.cfm, 'flow', 2);
        if (values.delta_t_f != null && !fields.tempDiff) patch.tempDiff = dtFromImp(values.delta_t_f);
        if (values.q_btuh != null && !fields.heatLoad) patch.heatLoad = fromImp(values.q_btuh, 'heatRate', 2);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Flow (${unit('flow')})`} value={fields.cfm} onChangeText={(v) => set({ cfm: v })} rightText={unit('flow')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Temperature Difference (${dtUnit})`} value={fields.tempDiff} onChangeText={(v) => set({ tempDiff: v })} rightText={dtUnit} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label={`Heat Load (${unit('heatRate')})`} value={fields.heatLoad} onChangeText={(v) => set({ heatLoad: v })} rightText={unit('heatRate')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default HeatTransferQBtuhScreen;
